/**
 * Admin › Workspaces: a narrow list on the left, one workspace on the right.
 *
 * The list carries three things per row (who, credits used in 30 days, last
 * activity) plus search and sort. Everything else about a workspace lives on
 * the detail as tabs, and the URL tracks both the selection and the tab
 * (`/admin/workspaces/:id/:tab`) so a row on the Overview leaderboard or a
 * pasted link lands on the right place.
 *
 * Usage, Controls and Billing mount their existing panels unchanged. Members
 * and Brief are the old inline expanders given their own room. Credits is the
 * per-workspace slice of the grant report with the Grant credits action where
 * you are already looking at the workspace. Flags is a read-only view of this
 * workspace's overrides; editing stays on Feature flags so there is one writer.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type {
  AdminWorkspaceStatusCounts,
  AdminWorkspaceStatusFilter,
  CreditGrantReport,
  FeatureFlag,
  FeatureFlagOverride,
} from "@yagni/shared";
import {
  CheckCircle2,
  Clock,
  Coins,
  Eye,
  Flag,
  Gift,
  Loader2,
  MoreHorizontal,
  Search,
  Server,
  Trash2,
  Users,
  XCircle,
} from "lucide-react";
import { Button } from "../../ui/button.js";
import { GrantCreditsModal } from "../../components/admin/GrantCreditsModal.js";
import { WorkspaceControlsPanel } from "../../components/admin/WorkspaceControlsPanel.js";
import { AdminWorkspaceUsagePanel } from "../../components/AdminWorkspaceUsagePanel.js";
import { AUTHENTICATED_DEFAULT_PATH } from "../../routes/defaults.js";
import { enterGodMode } from "../../lib/godMode.js";
import {
  EditionPublishSchedule,
  EditionSlot,
  LoadingSpinner,
  SectionHeader,
  SubTabs,
  Table,
  Td,
  Th,
  WorkspaceRow,
  WorkspaceUserRow,
  formatCredits,
  formatCreditsCompact,
  timeAgo,
  workspacePath,
} from "./shared.js";

export type WorkspaceTab = "usage" | "members" | "credits" | "controls" | "brief" | "flags";

export const WORKSPACE_TABS: ReadonlyArray<{ key: WorkspaceTab; label: string }> = [
  { key: "usage", label: "Usage" },
  { key: "members", label: "Members" },
  { key: "credits", label: "Credits" },
  { key: "controls", label: "Controls" },
  { key: "brief", label: "Brief" },
  { key: "flags", label: "Flags" },
];

export function isWorkspaceTab(value: string | undefined): value is WorkspaceTab {
  return WORKSPACE_TABS.some((t) => t.key === value);
}

type SortKey = "credits" | "activity" | "created" | "name" | "granted";

const SORTS: Array<{ key: SortKey; label: string }> = [
  { key: "credits", label: "Credits, 30d" },
  { key: "activity", label: "Last activity" },
  { key: "created", label: "Newest" },
  { key: "granted", label: "Credits granted" },
  { key: "name", label: "Name" },
];

const PAGE_SIZE = 50;
/** The list's columns: name, then owner and members from md up, credits, last seen. */
const LIST_COLUMNS =
  "grid-cols-[minmax(0,1fr)_72px_56px] md:grid-cols-[minmax(0,2fr)_minmax(0,1.5fr)_72px_96px_72px]";
/** The server's page cap; a refresh asks for at most this many rows at once. */
const PAGE_MAX = 200;
const SEARCH_DEBOUNCE_MS = 250;

/** One page of GET /api/admin/workspaces?limit=…; the server sorts, searches and filters by status. */
export interface WorkspaceListPage {
  workspaces: WorkspaceRow[];
  nextCursor: string | null;
  total: number;
  active30d: number;
  maxCreditsUsed30d: number;
  /** Every workspace by liveness; absent from a backend older than the status filter. */
  counts?: AdminWorkspaceStatusCounts;
}

const STATUS_FILTERS: Array<{ key: AdminWorkspaceStatusFilter; label: string }> = [
  { key: "live", label: "Live" },
  { key: "deactivated", label: "Deactivated" },
  { key: "all", label: "All" },
];

/** Suspended or terminated: off Systems, Stream and the default list. */
function isDeactivated(w: WorkspaceRow): boolean {
  return w.status === "suspended" || w.status === "terminated";
}

function creditsUsed30d(w: WorkspaceRow): number {
  return Number(w.credits_used_30d ?? 0) || 0;
}

function sortWorkspaces(rows: WorkspaceRow[], sort: SortKey): WorkspaceRow[] {
  const sorted = [...rows];
  const time = (v: string | null) => (v ? new Date(v).getTime() : 0);
  sorted.sort((a, b) => {
    switch (sort) {
      case "credits":
        return creditsUsed30d(b) - creditsUsed30d(a) || a.name.localeCompare(b.name);
      case "activity":
        return time(b.last_activity_at) - time(a.last_activity_at) || a.name.localeCompare(b.name);
      case "created":
        return time(b.created_at) - time(a.created_at);
      case "granted":
        return Number(b.credits_granted ?? 0) - Number(a.credits_granted ?? 0) || a.name.localeCompare(b.name);
      case "name":
      default:
        return a.name.localeCompare(b.name);
    }
  });
  return sorted;
}

/**
 * A backend from before pagination (one still serving mid-deploy) ignores the
 * page params and answers with every workspace. Search and sort that here so
 * the list reads the same either way.
 */
function toListPage(body: unknown, search: string, sort: SortKey): WorkspaceListPage {
  if (!Array.isArray(body)) return body as WorkspaceListPage;
  const all = body as WorkspaceRow[];
  const q = search.toLowerCase();
  const filtered = q
    ? all.filter(
        (w) =>
          w.name.toLowerCase().includes(q) ||
          w.slug.toLowerCase().includes(q) ||
          (w.first_user_email ?? "").toLowerCase().includes(q),
      )
    : all;
  return {
    workspaces: sortWorkspaces(filtered, sort),
    nextCursor: null,
    total: all.length,
    active30d: all.filter((w) => creditsUsed30d(w) > 0).length,
    maxCreditsUsed30d: Math.max(0, ...all.map(creditsUsed30d)),
  };
}

/** The list on screen and the sort and search that produced it. */
type LoadedList = WorkspaceListPage & { query: string };

function queryKey(sort: SortKey, search: string, status: AdminWorkspaceStatusFilter): string {
  return `${sort}\n${search}\n${status}`;
}

function listUrl(
  sort: SortKey,
  search: string,
  status: AdminWorkspaceStatusFilter,
  cursor: string | null,
  limit = PAGE_SIZE,
): string {
  const params = new URLSearchParams({ limit: String(limit), sort, status });
  if (search) params.set("q", search);
  if (cursor) params.set("cursor", cursor);
  return `/api/admin/workspaces?${params.toString()}`;
}

export function WorkspacesPage({ workspaceId, tab }: { workspaceId?: string; tab: WorkspaceTab }) {
  const [list, setList] = useState<LoadedList | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("credits");
  const [status, setStatus] = useState<AdminWorkspaceStatusFilter>("live");
  const [params] = useSearchParams();
  const scope = params.get("ws");
  const keepScope = scope ? `?ws=${encodeURIComponent(scope)}` : "";
  // Bumped by every first-page fetch, so a slow answer for an old search or
  // sort (or a load-more that started before it) never lands on the new list.
  const generation = useRef(0);
  const currentQuery = queryKey(sort, search, status);
  const loadedCount = useRef(0);
  loadedCount.current = list?.workspaces.length ?? 0;

  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // `limit` above one page is a refresh: it re-reads the rows already loaded so
  // a change made from the detail does not collapse the list to page one.
  const reload = useCallback(async (limit = PAGE_SIZE) => {
    const mine = ++generation.current;
    try {
      const res = await fetch(listUrl(sort, search, status, null, limit));
      if (mine !== generation.current) return;
      if (!res.ok) {
        setLoadError(`The workspace list failed to load (HTTP ${res.status}).`);
        return;
      }
      const page = toListPage(await res.json(), search, sort);
      if (mine !== generation.current) return;
      setList({ ...page, query: queryKey(sort, search, status) });
      setLoadError(null);
    } catch (err) {
      if (mine !== generation.current) return;
      setLoadError(`The workspace list failed to load: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [sort, search, status]);

  useEffect(() => {
    void reload().finally(() => setLoading(false));
  }, [reload]);

  // A cursor belongs to the sort and search that produced it. Until the first
  // page of a new sort or search arrives there is nothing more to load, so a
  // Load more in that gap never pairs the old cursor with the new query.
  const nextCursor = list && list.query === currentQuery ? list.nextCursor : null;
  const loadingMoreNow = useRef(false);
  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMoreNow.current) return;
    const mine = generation.current;
    loadingMoreNow.current = true;
    setLoadingMore(true);
    try {
      const res = await fetch(listUrl(sort, search, status, nextCursor));
      if (mine !== generation.current) return;
      if (!res.ok) {
        setLoadError(`More workspaces failed to load (HTTP ${res.status}).`);
        return;
      }
      const page = toListPage(await res.json(), search, sort);
      if (mine !== generation.current) return;
      setList((prev) => {
        if (!prev) return { ...page, query: queryKey(sort, search, status) };
        const seen = new Set(prev.workspaces.map((w) => w.id));
        return { ...page, query: prev.query, workspaces: [...prev.workspaces, ...page.workspaces.filter((w) => !seen.has(w.id))] };
      });
      setLoadError(null);
    } catch (err) {
      if (mine !== generation.current) return;
      setLoadError(`More workspaces failed to load: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      loadingMoreNow.current = false;
      setLoadingMore(false);
    }
  }, [nextCursor, sort, search, status]);

  // Infinite scroll: reaching the bottom of the list loads the next page. The
  // Load more button below does the same for keyboards and old browsers.
  const scrollRoot = useRef<HTMLDivElement | null>(null);
  const sentinel = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;
  useEffect(() => {
    const target = sentinel.current;
    if (!target || !nextCursor || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void loadMoreRef.current();
      },
      { root: scrollRoot.current, rootMargin: "120px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [nextCursor]);

  const workspaces = list?.workspaces ?? [];
  const listed = workspaceId ? (workspaces.find((w) => w.id === workspaceId) ?? null) : null;

  // A deep link to a workspace past the loaded pages (or outside the search)
  // fetches that one row, so the detail opens wherever the list stands. A 404
  // is "no such workspace"; anything else is a failed load the admin can retry.
  const [singleVersion, setSingleVersion] = useState(0);
  const [single, setSingle] = useState<{ key: string; row: WorkspaceRow | null; error: string | null } | null>(null);
  const singleKey = workspaceId ? `${workspaceId}:${singleVersion}` : null;
  const needsSingle = Boolean(workspaceId && list && !listed);
  useEffect(() => {
    if (!needsSingle || !workspaceId || !singleKey) return;
    let cancelled = false;
    fetch(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}`)
      .then(async (res) => {
        if (res.status === 404) {
          if (!cancelled) setSingle({ key: singleKey, row: null, error: null });
          return;
        }
        if (!res.ok) {
          if (!cancelled) setSingle({ key: singleKey, row: null, error: `The workspace failed to load (HTTP ${res.status}).` });
          return;
        }
        const body = (await res.json()) as WorkspaceRow;
        if (!cancelled) setSingle({ key: singleKey, row: body && body.id === workspaceId ? body : null, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setSingle({ key: singleKey, row: null, error: `The workspace failed to load: ${message}` });
      });
    return () => {
      cancelled = true;
    };
  }, [needsSingle, workspaceId, singleKey]);

  const fetchedSingle = single && single.key === singleKey ? single : null;
  const fresh = listed ?? fetchedSingle?.row ?? null;
  const singleError = !listed ? (fetchedSingle?.error ?? null) : null;
  // While the same workspace is re-read after a change, keep showing the last
  // copy so the detail and its open tab stay mounted; only a new id waits.
  // The ref is written after commit, so a discarded render never moves it.
  const lastSelected = useRef<WorkspaceRow | null>(null);
  const waiting = Boolean(workspaceId && !listed && !fetchedSingle);
  const stale = waiting && lastSelected.current?.id === workspaceId ? lastSelected.current : null;
  const selected = fresh ?? stale;
  useEffect(() => {
    if (fresh) lastSelected.current = fresh;
    else if (!waiting) lastSelected.current = null;
  }, [fresh, waiting]);
  const selectedPending = waiting && !stale;
  const retrySingle = useCallback(() => setSingleVersion((v) => v + 1), []);

  const onWorkspacesChanged = useCallback(async () => {
    setSingleVersion((v) => v + 1);
    await reload(Math.min(Math.max(loadedCount.current, PAGE_SIZE), PAGE_MAX));
  }, [reload]);

  const maxCredits = Math.max(1, list?.maxCreditsUsed30d ?? 0);
  const total = list?.total ?? 0;
  const active30d = list?.active30d ?? 0;

  function renderDetail() {
    if (selectedPending) return <LoadingSpinner />;
    if (selected) return <WorkspaceDetail workspace={selected} tab={tab} onWorkspacesChanged={onWorkspacesChanged} />;
    if (singleError) {
      return (
        <div role="alert" className="p-8 text-sm text-center flex flex-col items-center gap-3" style={{ color: "var(--text-secondary)" }}>
          <span>{singleError}</span>
          <Button type="button" size="sm" variant="secondary" onClick={retrySingle}>
            Retry
          </Button>
        </div>
      );
    }
    return (
      <div className="p-8 text-sm text-center" style={{ color: "var(--text-secondary)" }}>
        No workspace with id <code className="font-mono text-[12px]">{workspaceId}</code>.
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;
  if (loadError && !list) {
    return (
      <div
        role="alert"
        className="rounded-[var(--radius-card)] border-2 p-4 text-sm"
        style={{ borderColor: "var(--honesty-border)", background: "var(--honesty-bg)", color: "var(--text-secondary)" }}
      >
        {loadError}
      </div>
    );
  }

  // The list stays mounted while a workspace is open, so Back returns to the
  // same search, status chip, loaded pages and scroll.
  return (
    <div className="flex flex-col gap-4">
      {workspaceId && (
        <div className="flex flex-col gap-3" data-testid="workspace-detail">
          <div>
            <Link className="btn quiet sm" style={{ textDecoration: "none" }} to={{ pathname: "/admin/workspaces", search: keepScope }}>
              ← All workspaces
            </Link>
          </div>
          <div
            className="rounded-[var(--radius-card)] overflow-hidden min-w-0"
            style={{ background: "var(--print-bg)", border: "1px solid var(--print-border)", boxShadow: "var(--shadow-print)" }}
          >
            {renderDetail()}
          </div>
        </div>
      )}
      <div
        hidden={Boolean(workspaceId)}
        className="rounded-[var(--radius-card)] overflow-hidden flex flex-col"
        style={{ background: "var(--print-bg)", border: "1px solid var(--print-border)", boxShadow: "var(--shadow-print)" }}
        aria-label={`${total} workspaces, ${active30d} active in the last 30 days`}
      >
        <div className="flex gap-2 p-2.5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
          <label className="flex-1 min-w-0 flex items-center gap-1.5 rounded-md border px-2" style={{ borderColor: "var(--border)", background: "var(--bg-input)" }}>
            <Search size={12} style={{ color: "var(--text-tertiary)" }} />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, slug, owner email"
              aria-label="Search workspaces"
              className="flex-1 min-w-0 bg-transparent border-0 py-1.5 text-[12.5px] focus:outline-none"
              style={{ color: "var(--text-primary)" }}
            />
          </label>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            aria-label="Sort workspaces"
            className="rounded-md border px-1.5 text-[12px]"
            style={{ borderColor: "var(--border)", background: "var(--print-bg-solid)", color: "var(--text-secondary)" }}
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <div className="rhythm px-2.5 py-2 border-b" role="group" aria-label="Workspace status" style={{ borderColor: "var(--border-subtle)" }}>
          {STATUS_FILTERS.map((f) => {
            const count = f.key === "all" ? null : list?.counts?.[f.key];
            return (
              <button
                key={f.key}
                type="button"
                className={status === f.key ? "chip on" : "chip"}
                aria-pressed={status === f.key}
                onClick={() => setStatus(f.key)}
              >
                {f.label}
                {count !== null && count !== undefined ? <b> {count}</b> : null}
              </button>
            );
          })}
        </div>
        <div
          className={`grid gap-3 px-3 py-1.5 text-[9.5px] uppercase tracking-widest border-b ${LIST_COLUMNS}`}
          style={{ color: "var(--text-tertiary)", borderColor: "var(--border-subtle)", fontFamily: "var(--font-mono)" }}
        >
          <span>
            {total} · {active30d} active
          </span>
          <span className="hidden md:block">Owner</span>
          <span className="hidden md:block text-right">Members</span>
          <span className="text-right">Credits 30d</span>
          <span className="text-right">Seen</span>
        </div>
        <div ref={scrollRoot} className="max-h-[70vh] overflow-y-auto" data-testid="workspace-list">
          {workspaces.length === 0 && (
            <div className="px-3 py-4 text-xs" style={{ color: "var(--text-secondary)" }}>
              {status === "deactivated" && !search
                ? "No workspace is deactivated. Suspend one from its Controls tab."
                : "No workspaces match."}
            </div>
          )}
          {workspaces.map((w) => {
            const isActive = w.id === workspaceId;
            const credits = creditsUsed30d(w);
            return (
              <Link
                key={w.id}
                to={workspacePath(w.id, tab)}
                aria-current={isActive ? "true" : undefined}
                className={`grid gap-3 items-center px-3 py-2 border-b no-underline ${LIST_COLUMNS}`}
                style={{
                  borderColor: "var(--border-subtle)",
                  background: isActive ? "var(--accent-muted)" : "transparent",
                  boxShadow: isActive ? "inset 3px 0 0 var(--accent)" : undefined,
                  color: "var(--text-primary)",
                }}
              >
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 text-[13px] font-semibold truncate">
                    <span
                      aria-hidden
                      className="inline-block w-[7px] h-[7px] rounded-full shrink-0"
                      style={{ background: w.status === "active" ? "var(--success)" : "var(--text-tertiary)" }}
                    />
                    <span className="truncate">{w.name}</span>
                  </span>
                  <span className="block text-[10px] truncate" style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
                    {w.slug}
                    {w.is_internal ? " · internal" : ""}
                    {isDeactivated(w) ? ` · ${w.status === "terminated" ? "deleted" : "deactivated"}` : ""}
                  </span>
                </span>
                <span className="hidden md:block min-w-0 truncate text-[12px]" style={{ color: "var(--text-secondary)" }}>
                  {w.first_user_email ?? "no owner"}
                </span>
                <span className="hidden md:block text-right text-[12px] tabular-nums" style={{ fontFamily: "var(--font-mono)" }}>
                  {w.member_count}
                </span>
                <span className="text-right text-[12px] tabular-nums" style={{ fontFamily: "var(--font-mono)" }}>
                  {formatCreditsCompact(credits)}
                  <span className="block h-[3px] rounded mt-1 overflow-hidden relative" style={{ background: "var(--accent-subtle)" }} aria-hidden>
                    <span
                      className="absolute inset-y-0 left-0"
                      style={{ width: `${Math.min(100, (credits / maxCredits) * 100)}%`, background: w.is_internal ? "var(--text-tertiary)" : "var(--accent)" }}
                    />
                  </span>
                </span>
                <span className="text-right text-[10.5px]" style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
                  {w.last_activity_at ? timeAgo(w.last_activity_at).replace(" ago", "") : "never"}
                </span>
              </Link>
            );
          })}
          {loadError && (
            <div role="alert" className="px-3 py-2 text-[11.5px]" style={{ color: "var(--text-secondary)", background: "var(--honesty-bg)" }}>
              {loadError}
            </div>
          )}
          {nextCursor && (
            <div ref={sentinel} className="p-2 flex justify-center">
              <Button type="button" size="sm" variant="secondary" onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore && <Loader2 size={12} className="animate-spin" />}
                Load more
              </Button>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

/* ─── Detail ────────────────────────────────────────────────── */

function WorkspaceDetail({
  workspace: w,
  tab,
  onWorkspacesChanged,
}: {
  workspace: WorkspaceRow;
  tab: WorkspaceTab;
  onWorkspacesChanged: () => Promise<void>;
}) {
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantVersion, setGrantVersion] = useState(0);
  const [entering, setEntering] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleEnterGodMode() {
    setEntering(true);
    try {
      const result = await enterGodMode(w.id, AUTHENTICATED_DEFAULT_PATH);
      if (!result.ok) alert(`Failed to enter god mode: ${result.error}`);
    } finally {
      setEntering(false);
    }
  }

  async function handleDelete() {
    const confirmed = window.confirm(
      `Delete workspace "${w.name}" (${w.slug})?\n\nThis will remove all workspace data and cannot be undone.`,
    );
    if (!confirmed) return;
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/workspaces/${w.id}`, { method: "DELETE" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: "Unknown error" }));
        alert(`Failed to delete: ${body.error}`);
        return;
      }
      await onWorkspacesChanged();
    } finally {
      setDeleting(false);
    }
  }

  const tabs = WORKSPACE_TABS.map((t) => ({ key: t.key, label: t.label, to: workspacePath(w.id, t.key) }));

  return (
    <div>
      <div className="px-4 pt-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold m-0" style={{ color: "var(--text-primary)", fontFamily: "var(--font-display)" }}>
              {w.name}
            </h2>
            <div className="flex flex-wrap gap-x-3 text-[11px] mt-0.5" style={{ color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>
              <span>{w.slug}</span>
              <span>{w.plan}</span>
              <span>{w.status}</span>
              {w.first_user_email && <span>{w.first_user_email}</span>}
              <span>since {new Date(w.created_at).toLocaleDateString()}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Button type="button" size="sm" onClick={() => setGrantOpen(true)}>
              <Gift size={12} /> Grant credits
            </Button>
            <Button type="button" size="sm" variant="secondary" onClick={() => void handleEnterGodMode()} disabled={entering}>
              {entering ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
              View as workspace
            </Button>
            {w.status !== "terminated" && (
              <details className="relative">
                <summary
                  className="list-none cursor-pointer inline-flex items-center px-2 py-1.5 rounded-md border"
                  style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}
                  aria-label="More actions"
                >
                  <MoreHorizontal size={14} />
                </summary>
                <div
                  className="absolute right-0 mt-1 z-10 rounded-md border p-1 min-w-[180px]"
                  style={{ borderColor: "var(--border)", background: "var(--print-bg-solid)", boxShadow: "var(--shadow-print)" }}
                >
                  <button
                    type="button"
                    onClick={() => void handleDelete()}
                    disabled={deleting}
                    className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded text-[12.5px] border-0 bg-transparent cursor-pointer hover:bg-[var(--error-muted)] disabled:opacity-50"
                    style={{ color: "var(--error)" }}
                  >
                    {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    Delete workspace
                  </button>
                </div>
              </details>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <Fact label="Members" value={w.member_count} />
          <Fact label="Used, 30d" value={formatCreditsCompact(creditsUsed30d(w))} />
          <Fact label="Granted" value={formatCreditsCompact(Number(w.credits_granted ?? 0))} />
          <Fact label="Last active" value={w.last_activity_at ? timeAgo(w.last_activity_at) : "Never"} />
        </div>

        <SubTabs tabs={tabs} active={tab} label="Workspace sections" />
      </div>

      <div className="p-4">
        {tab === "usage" && <AdminWorkspaceUsagePanel workspaceId={w.id} />}
        {tab === "members" && <MembersTab workspace={w} onWorkspacesChanged={onWorkspacesChanged} />}
        {tab === "credits" && <CreditsTab workspace={w} version={grantVersion} onGrant={() => setGrantOpen(true)} />}
        {tab === "controls" && <WorkspaceControlsPanel workspaceId={w.id} />}
        {tab === "brief" && <BriefTab workspace={w} />}
        {tab === "flags" && <FlagsTab workspace={w} />}
      </div>

      <GrantCreditsModal
        isOpen={grantOpen}
        workspaces={[{ id: w.id, name: w.name, slug: w.slug, firstUserEmail: w.first_user_email }]}
        initialWorkspaceId={w.id}
        onClose={() => setGrantOpen(false)}
        onCreated={() => {
          setGrantVersion((v) => v + 1);
          void onWorkspacesChanged();
        }}
      />
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-md border px-2.5 py-2" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-base)" }}>
      <div className="text-[11px] truncate" style={{ color: "var(--text-tertiary)" }}>
        {label}
      </div>
      <div className="text-[13px] font-semibold tabular-nums" style={{ color: "var(--text-primary)", fontFamily: "var(--font-mono)" }}>
        {value}
      </div>
    </div>
  );
}

/* ─── Members ───────────────────────────────────────────────── */

function MembersTab({ workspace: w, onWorkspacesChanged }: { workspace: WorkspaceRow; onWorkspacesChanged: () => Promise<void> }) {
  const [users, setUsers] = useState<WorkspaceUserRow[] | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setUsers(null);
    fetch(`/api/admin/workspaces/${encodeURIComponent(w.id)}/users`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({ error: "Unknown error" }));
          alert(`Failed to load users: ${body.error}`);
          return [];
        }
        return (await res.json()) as WorkspaceUserRow[];
      })
      .then((rows) => {
        if (!cancelled) setUsers(rows);
      });
    return () => {
      cancelled = true;
    };
  }, [w.id]);

  async function handleDeleteUser(user: WorkspaceUserRow) {
    const confirmed = window.confirm(
      `Hard-delete user "${user.email}" from staging?\n\nThis deletes the user identity so the same OAuth account can sign up again. Sole-member workspaces for this user will also be deleted. This cannot be undone.`,
    );
    if (!confirmed) return;

    setDeletingUserId(user.id);
    try {
      const res = await fetch(`/api/admin/users/${user.id}`, { method: "DELETE" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: "Unknown error" }));
        alert(`Failed to delete user: ${body.error}`);
        return;
      }
      const body = (await res.json()) as { deletedUserId: string; deletedWorkspaces?: string[] };
      setUsers((prev) => (prev ?? []).filter((candidate) => candidate.id !== body.deletedUserId));
      // Member counts (and possibly this workspace itself) changed.
      await onWorkspacesChanged();
    } finally {
      setDeletingUserId(null);
    }
  }

  if (users === null) return <LoadingSpinner />;

  return (
    <div>
      <SectionHeader title="Members" icon={<Users size={16} style={{ color: "var(--info)" }} />} />
      {users.length === 0 ? (
        <div className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
          No members in this workspace.
        </div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Email</Th>
              <Th>Name</Th>
              <Th>Role</Th>
              <Th>Verified</Th>
              <Th>Joined</Th>
              <Th>Last session</Th>
              <Th>Action</Th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <Td mono>{u.email}</Td>
                <Td>{u.display_name}</Td>
                <Td>{u.role}</Td>
                <Td>
                  {u.email_verified ? (
                    <CheckCircle2 size={12} style={{ color: "var(--success)" }} />
                  ) : (
                    <XCircle size={12} style={{ color: "var(--text-tertiary)" }} />
                  )}
                </Td>
                <Td>{timeAgo(u.joined_at)}</Td>
                <Td>{u.last_session_at ? timeAgo(u.last_session_at) : "Never"}</Td>
                <Td>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={`Delete ${u.email}`}
                    onClick={() => void handleDeleteUser(u)}
                    disabled={deletingUserId === u.id}
                    className="text-[var(--error)] hover:bg-[var(--error-muted)]"
                  >
                    {deletingUserId === u.id ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />}
                    Delete
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

/* ─── Credits ───────────────────────────────────────────────── */

const CREDIT_REASON_LABELS: Record<string, string> = {
  beta_feedback: "Beta feedback",
  goodwill: "Goodwill",
  internal: "Internal",
  investor: "Investor",
  marketing: "Marketing",
};

function CreditsTab({ workspace: w, version, onGrant }: { workspace: WorkspaceRow; version: number; onGrant: () => void }) {
  const [report, setReport] = useState<CreditGrantReport | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setReport(undefined);
    fetch(`/api/admin/credit-grants/report?workspaceId=${encodeURIComponent(w.id)}`)
      .then((r) => (r.ok ? (r.json() as Promise<CreditGrantReport>) : null))
      .catch(() => null)
      .then((rep) => {
        if (!cancelled) setReport(rep);
      });
    return () => {
      cancelled = true;
    };
  }, [w.id, version]);

  if (report === undefined) return <LoadingSpinner />;
  if (report === null) {
    return (
      <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
        Unable to load the grant report.
      </div>
    );
  }

  const ws = report.byWorkspace.find((row) => row.workspaceId === w.id) ?? null;
  const used30d = creditsUsed30d(w);
  const remaining = ws?.remainingCredits ?? 0;
  const runway = used30d > 0 && remaining > 0 ? Math.round(remaining / (used30d / 30)) : null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <Fact label="Used, 30d" value={formatCreditsCompact(used30d)} />
        <Fact label="Granted" value={formatCredits(ws?.grantedCredits ?? 0)} />
        <Fact label="Consumed" value={formatCredits(ws?.consumedCredits ?? 0)} />
        <Fact label="Remaining" value={`${formatCredits(remaining)}${runway !== null ? ` · ~${runway}d` : ""}`} />
      </div>

      <div
        className="flex items-center justify-between gap-3 rounded-md border border-dashed px-3 py-2.5"
        style={{ borderColor: "var(--accent-border)", background: "var(--accent-subtle)" }}
      >
        <div className="text-[12.5px]" style={{ color: "var(--text-secondary)" }}>
          Grants land in the ledger below and in the platform report on Overview › Spend.
        </div>
        <Button type="button" size="sm" onClick={onGrant} data-testid="grant-credits-inline" className="shrink-0 whitespace-nowrap">
          <Gift size={12} /> Grant credits
        </Button>
      </div>

      <SectionHeader title="Grant history" icon={<Coins size={16} style={{ color: "var(--accent)" }} />} />
      {!ws || ws.grants.length === 0 ? (
        <div className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
          No credit grants for this workspace yet.
        </div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Date</Th>
              <Th>Amount</Th>
              <Th>Reason</Th>
              <Th>Note</Th>
              <Th>Granted by</Th>
              <Th>Consumed / Remaining</Th>
            </tr>
          </thead>
          <tbody>
            {ws.grants.map((g) => (
              <tr key={g.id}>
                <Td mono>{new Date(g.createdAt).toLocaleDateString()}</Td>
                <Td mono>{formatCredits(g.amountCredits)}</Td>
                <Td>{CREDIT_REASON_LABELS[g.reason] ?? g.reason}</Td>
                <Td>{g.note ?? "—"}</Td>
                <Td>{g.grantedByDisplayName ?? "—"}</Td>
                <Td mono>
                  {formatCredits(g.consumedCredits)} / {formatCredits(g.remainingCredits)}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

/* ─── Brief (edition schedules + timezone) ──────────────────── */

const SLOT_ORDER: EditionSlot[] = ["morning", "midday", "evening"];

function BriefTab({ workspace: w }: { workspace: WorkspaceRow }) {
  const [schedules, setSchedules] = useState<EditionPublishSchedule[] | null>(null);
  const [savingSlot, setSavingSlot] = useState<EditionSlot | null>(null);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [tzDraft, setTzDraft] = useState("");
  const [tzSaving, setTzSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSchedules(null);
    setTimezone(null);
    fetch(`/api/admin/edition-publish/workspaces/${encodeURIComponent(w.id)}/schedules`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({ error: "Unknown error" }));
          alert(`Failed to load Brief schedules: ${body.error}`);
          return { schedules: [], timezone: null };
        }
        return (await res.json()) as { schedules: EditionPublishSchedule[]; timezone: string | null };
      })
      .then((body) => {
        if (cancelled) return;
        setSchedules(body.schedules);
        setTimezone(body.timezone ?? "");
        setTzDraft(body.timezone ?? "");
      });
    return () => {
      cancelled = true;
    };
  }, [w.id]);

  async function updateSchedule(slot: EditionSlot, enabled: boolean) {
    setSavingSlot(slot);
    try {
      const res = await fetch(
        `/api/admin/edition-publish/workspaces/${encodeURIComponent(w.id)}/schedules/${slot}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ enabled }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: "Unknown error" }));
        alert(`Failed to update Brief schedule: ${body.error}`);
        return;
      }
      const body = (await res.json()) as { schedule: EditionPublishSchedule };
      setSchedules((prev) => {
        const next = [...(prev ?? [])];
        const index = next.findIndex((s) => s.slot === slot);
        if (index >= 0) next[index] = body.schedule;
        else next.push(body.schedule);
        return next;
      });
    } finally {
      setSavingSlot(null);
    }
  }

  async function saveTimezone() {
    const draft = tzDraft.trim();
    if (!draft) {
      alert("Timezone cannot be empty");
      return;
    }
    setTzSaving(true);
    try {
      const res = await fetch(`/api/admin/workspaces/${encodeURIComponent(w.id)}/timezone`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ timezone: draft }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: "Unknown error" }));
        alert(`Failed to update timezone: ${body.error}`);
        return;
      }
      const body = (await res.json()) as { workspace: { id: string; timezone: string } };
      setTimezone(body.workspace.timezone);
      setTzDraft(body.workspace.timezone);
    } finally {
      setTzSaving(false);
    }
  }

  if (schedules === null) return <LoadingSpinner />;

  const sorted = [...schedules].sort((a, b) => SLOT_ORDER.indexOf(a.slot) - SLOT_ORDER.indexOf(b.slot));
  const dirty = timezone !== null && tzDraft.trim() !== timezone;

  return (
    <div className="space-y-5">
      <div>
        <SectionHeader title="Brief publishing" icon={<Clock size={16} style={{ color: "var(--accent)" }} />} />
        <div className="flex flex-wrap gap-2">
          {sorted.map((schedule) => {
            const saving = savingSlot === schedule.slot;
            return (
              <button
                key={schedule.slot}
                type="button"
                onClick={() => void updateSchedule(schedule.slot, !schedule.enabled)}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs disabled:opacity-50"
                style={{
                  borderColor: "var(--border)",
                  background: schedule.enabled ? "var(--accent-muted)" : "rgba(var(--glass-rgb), 0.55)",
                  color: schedule.enabled ? "var(--accent)" : "var(--text-secondary)",
                }}
              >
                {saving && <Loader2 size={12} className="animate-spin" />}
                <span className="font-medium capitalize">{schedule.slot}</span>
                <span>{schedule.publish_time}</span>
                <span>{schedule.enabled ? "On" : "Off"}</span>
              </button>
            );
          })}
          {sorted.length === 0 && (
            <span className="text-xs" style={{ color: "var(--text-tertiary)" }}>
              No Brief schedules configured.
            </span>
          )}
        </div>
      </div>
      <div>
        <SectionHeader title="Workspace timezone" icon={<Server size={16} style={{ color: "var(--info)" }} />} />
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            value={tzDraft}
            placeholder="e.g. America/New_York"
            aria-label="Workspace timezone"
            onChange={(e) => setTzDraft(e.target.value)}
            disabled={tzSaving}
            className="rounded-md border px-2 py-1 text-xs disabled:opacity-50"
            style={{ borderColor: "var(--border)", background: "var(--bg-input)", color: "var(--text-primary)", minWidth: "16rem" }}
          />
          <button
            type="button"
            onClick={() => void saveTimezone()}
            disabled={tzSaving || !dirty}
            className="inline-flex items-center gap-1 rounded-md border px-3 py-1 text-xs disabled:opacity-50"
            style={{ borderColor: "var(--border)", background: "var(--accent-muted)", color: "var(--accent)" }}
          >
            {tzSaving && <Loader2 size={12} className="animate-spin" />}
            Save
          </button>
          <span className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
            IANA zone (e.g. UTC, America/Denver, Europe/Berlin)
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── Flags (read-only overrides for this workspace) ────────── */

function FlagsTab({ workspace: w }: { workspace: WorkspaceRow }) {
  const [state, setState] = useState<{ flags: FeatureFlag[]; overrides: FeatureFlagOverride[] } | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setState(undefined);
    // A failed fetch is a failure, not an empty list: "follows every global
    // default" is a positive claim this tab must not make without the data.
    Promise.all([
      fetch("/api/admin/feature-flags").then(async (r) => {
        if (!r.ok) throw new Error(`flags HTTP ${r.status}`);
        return (await r.json()) as FeatureFlag[];
      }),
      fetch(`/api/admin/feature-flags/overrides?workspaceId=${encodeURIComponent(w.id)}`).then(async (r) => {
        if (!r.ok) throw new Error(`overrides HTTP ${r.status}`);
        return (await r.json()) as { overrides: FeatureFlagOverride[] };
      }),
    ])
      .then(([flags, body]) => {
        if (!cancelled) setState({ flags, overrides: body.overrides });
      })
      .catch(() => {
        if (!cancelled) setState(null);
      });
    return () => {
      cancelled = true;
    };
  }, [w.id]);

  if (state === undefined) return <LoadingSpinner />;
  if (state === null) {
    return (
      <div
        role="alert"
        className="rounded-md border-2 p-3 text-sm"
        style={{ borderColor: "var(--honesty-border)", background: "var(--honesty-bg)", color: "var(--text-secondary)" }}
      >
        Unable to load this workspace's flags. The overrides are unknown, not empty.
      </div>
    );
  }

  const defaults = new Map(state.flags.map((f) => [f.key, f.defaultEnabled]));

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Overrides for this workspace"
        icon={<Flag size={16} style={{ color: "var(--accent)" }} />}
        action={
          <Link to="/admin/feature-flags" className="text-[12px] font-medium">
            Edit on Feature flags →
          </Link>
        }
      />
      {state.overrides.length === 0 ? (
        <div className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
          No overrides. This workspace follows every global default.
        </div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Flag</Th>
              <Th>Global default</Th>
              <Th>This workspace</Th>
              <Th>Updated</Th>
            </tr>
          </thead>
          <tbody>
            {state.overrides.map((o) => (
              <tr key={o.flagKey}>
                <Td mono>{o.flagKey}</Td>
                <Td>{defaults.has(o.flagKey) ? (defaults.get(o.flagKey) ? "on" : "off") : "—"}</Td>
                <Td>
                  <span
                    className="px-2 py-0.5 rounded text-[11px] font-medium"
                    style={{
                      background: o.enabled ? "var(--accent-muted)" : "rgba(var(--ink-rgb), 0.06)",
                      color: o.enabled ? "var(--accent)" : "var(--text-tertiary)",
                    }}
                  >
                    {o.enabled ? "on" : "off"}
                  </span>
                </Td>
                <Td>{timeAgo(o.updatedAt)}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
